/**
 * File: PeerReviewProgressPanel.tsx
 * Description: Rich loading panel shown while Archie runs the peer-review
 *   debate. The assessment is a multi-minute, two-agent debate
 *   (Optimistic Archie argues each item is satisfied, Pessimistic Archie
 *   stress-tests it), so instead of a bare spinner this surfaces the actual
 *   reasoning stages, a progress bar and an elapsed-time counter. The backend
 *   does not (yet) stream real per-item progress, so the bar advances on an
 *   asymptotic time curve that never claims completion until the run finishes.
 */
import {
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentMagnifyingGlassIcon,
  ScaleIcon,
  ShieldExclamationIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useRef, useState } from 'react';

interface PeerReviewProgressPanelProps {
  /** Number of checklist items being assessed, shown in the caption. */
  itemCount?: number;
}

interface ProgressStage {
  key: string;
  label: string;
  detail: string;
  Icon: React.ComponentType<{ className?: string }>;
}

const STAGES: ProgressStage[] = [
  {
    key: 'context',
    label: 'Gathering evidence',
    detail:
      'Reading the architecture document, ADRs and the referenced ' +
      'enterprise guidance.',
    Icon: DocumentMagnifyingGlassIcon,
  },
  {
    key: 'optimistic',
    label: 'Optimistic Archie',
    detail:
      'Building the best good-faith case that each checklist item is ' +
      'satisfied, with citations.',
    Icon: SparklesIcon,
  },
  {
    key: 'pessimistic',
    label: 'Pessimistic Archie',
    detail:
      'Stress-testing the design for gaps, risks and missing evidence to ' +
      'rebut the optimistic view.',
    Icon: ShieldExclamationIcon,
  },
  {
    key: 'reconcile',
    label: 'Reconciling the debate',
    detail: 'Weighing both sides into a verdict and citations for each item.',
    Icon: ScaleIcon,
  },
];

const TICK_MS = 1000;
// Larger time-constant => the bar fills more slowly. ~75s gives ~63% at 75s,
// ~86% at 150s, approaching (but never reaching) the cap.
const PROGRESS_TIME_CONSTANT_S = 75;
const MAX_SIMULATED_PROGRESS = 95;
const GATHERING_STAGE_MAX_PROGRESS = 12;
const OPTIMISTIC_STAGE_MAX_PROGRESS = 55;
const PESSIMISTIC_STAGE_MAX_PROGRESS = 88;

/**
 * Map a progress percentage to the active stage index. Reading is quick, the
 * optimistic pass is the long middle, then the pessimistic rebuttal, then the
 * brief reconciliation.
 */
const stageForProgress = (progress: number): number => {
  if (progress < GATHERING_STAGE_MAX_PROGRESS) {
    return 0;
  }
  if (progress < OPTIMISTIC_STAGE_MAX_PROGRESS) {
    return 1;
  }
  if (progress < PESSIMISTIC_STAGE_MAX_PROGRESS) {
    return 2;
  }
  return STAGES.length - 1;
};

export const PeerReviewProgressPanel: React.FC<
  PeerReviewProgressPanelProps
> = ({ itemCount }) => {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef<number>(Date.now());

  useEffect(() => {
    startRef.current = Date.now();
    setElapsed(0);
    const intervalId = globalThis.setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, TICK_MS);
    return () => globalThis.clearInterval(intervalId);
  }, []);

  const progress = useMemo(() => {
    const raw = (1 - Math.exp(-elapsed / PROGRESS_TIME_CONSTANT_S)) * 100;
    return Math.min(MAX_SIMULATED_PROGRESS, Math.round(raw));
  }, [elapsed]);

  const activeStage = useMemo(() => stageForProgress(progress), [progress]);

  const caption =
    typeof itemCount === 'number' && itemCount > 0
      ? `Two debating agents are assessing ${itemCount} checklist items ` +
        'against enterprise guidance. This usually takes a few minutes.'
      : 'Two debating agents are assessing the checklist against enterprise ' +
        'guidance. This usually takes a few minutes.';

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="relative flex h-10 w-10 items-center justify-center">
          <span className="absolute inset-0 animate-ping rounded-full bg-emerald-400/30" />
          <ArrowPathIcon className="h-6 w-6 animate-spin text-emerald-600" />
        </span>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            Archie is reviewing your architecture
          </h3>
          <p className="mt-0.5 max-w-xl text-xs text-gray-500">{caption}</p>
        </div>
      </div>

      <div className="mt-4">
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-emerald-500 transition-all duration-700 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <ol className="mt-4 space-y-2.5">
        {STAGES.map((stage, index) => {
          const isDone = index < activeStage;
          const isActive = index === activeStage;
          const { Icon } = stage;
          let iconWrapperClass = 'bg-gray-100 text-gray-400';
          if (isDone) {
            iconWrapperClass = 'bg-emerald-100 text-emerald-600';
          } else if (isActive) {
            iconWrapperClass = 'bg-emerald-50 text-emerald-600';
          }
          return (
            <li key={stage.key} className="flex items-start gap-3">
              <span
                className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${iconWrapperClass}`}
              >
                {isDone ? (
                  <CheckCircleIcon className="h-4 w-4" />
                ) : (
                  <Icon
                    className={`h-4 w-4 ${isActive ? 'animate-pulse' : ''}`}
                  />
                )}
              </span>
              <div className="min-w-0">
                <p
                  className={`text-sm font-medium ${
                    isDone || isActive ? 'text-gray-900' : 'text-gray-400'
                  }`}
                >
                  {stage.label}
                </p>
                <p
                  className={`text-xs ${
                    isActive ? 'text-gray-600' : 'text-gray-400'
                  }`}
                >
                  {stage.detail}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};
